import classNames from "classnames"
import * as React from "react"

type StatusVariant = "status" | "neutral" | "positive" | "warning" | "error" | "info"

export interface StatusProps<T extends React.ElementType = "div"> {
    type: StatusVariant
    variant?: "filled" | "outline"
    as?: T
}

const statusTypeStyles: Record<StatusVariant, string> = {
    status: "bg-yellow-900 text-yellow-200 border-yellow-700",
    neutral: "bg-white-100 text-white-500 border-white-200",
    positive: "bg-green-900 text-green-200 border-green-700",
    warning: "bg-orange-900 text-orange-200 border-orange-700",
    error: "bg-red-900 text-red-200 border-red-700",
    info: "bg-blue-900 text-blue-200 border-blue-700",
}

const variantStyles: Record<"filled" | "outline", string> = {
    filled: "border-0 rounded-md py-1 px-2",
    outline: "border rounded-full py-0.5 px-2 bg-opacity-0",
}

export const Status = <T extends React.ElementType = "div">({
    variant = "outline",
    type,
    children,
    className,
    as,
    ...rest
}: React.PropsWithChildren<StatusProps<T>> &
    Omit<React.ComponentPropsWithoutRef<T>, keyof StatusProps<T>>) => {
    const Component = as ?? "small"
    return (
        <Component
            className={classNames(
                "w-fit",
                statusTypeStyles[type],
                variantStyles[variant],
                className
            )}
            {...rest}
        >
            {children}
        </Component>
    )
}
